import React from 'react';
import PropTypes from 'prop-types';
import {
  PLANNING,
  ONBOARDING,
  PROSPECT,
  ACTIVE,
  SERVICE,
} from 'Classes/Household';
import totalIcon from 'Assets/Images/total-clients.svg';
import onboardingIcon from 'Assets/Images/onboarding-clients.svg';
import planningIcon from 'Assets/Images/planning-clients.svg';
import escalatedIcon from 'Assets/Images/escalated-clients.svg';
import serviceIcon from 'Assets/Images/service-clients.svg';
import FontAwesome from 'Components/UI/FontAwesome/FontAwesome';
import { Link } from 'react-router-dom';
import { clientsUri } from 'Consts/advisorUris';
import withStyles from '@material-ui/core/styles/withStyles';
import classNames from 'classnames';
import { Typography } from '@material-ui/core';

const styles = theme => ({
  root: {
    height: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
  },
  title: {
    color: theme.palette.facet.primaryBlue,
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center',
    margin: theme.spacing(3),
    fontSize: theme.typography.h1.fontSize,
  },
  clientCounts: {
    display: 'flex',
    flexDirection: 'column',
    webkitUserSelect: 'none',
    mozUserSelect: 'none',
    msUserSelect: 'none',
    userSelect: 'none',
    justifyContent: 'space-between',
    height: '100%',
  },
  clientCount: {
    flex: '1 1 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  icon: {
    height: '46px',
    width: '46px',
    webkitBorderRadius: '100%',
    mozBorderRadius: '100%',
    borderRadius: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 'auto',
  },
  iconTotal: {
    backgroundColor: '#2d6786',
  },
  countValue: {
    fontSize: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& i': {
      fontSize: '20px',
      marginLeft: '5px',
    },
    '& a': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  totalValue: {
    color: theme.palette.facet.primaryBlue,
    '& i': {
      color: theme.palette.facet.primaryBlue,
    },
  },
  countLabel: {
    color: theme.palette.facet.primaryGray,
    fontWeight: '500',
    fontSize: '12px',
  },
  onboardingValue: {
    color: theme.palette.facet.accentBlue2,
    '& i': {
      color: theme.palette.facet.accentBlue2,
    },
  },
  iconOnboarding: {
    backgroundColor: theme.palette.facet.accentBlue2,
  },
  iconPlanning: {
    backgroundColor: theme.palette.facet.accentBlue3,
  },
  iconService: {
    backgroundColor: theme.palette.facet.accentGreen2,
  },
  iconEscalated: {
    backgroundColor: theme.palette.facet.orange,
  },
  planningValue: {
    color: theme.palette.facet.accentBlue,
    '& i': {
      color: theme.palette.facet.accentBlue,
    },
  },
  escalatedValue: {
    color: theme.palette.facet.orange,
    '& i': {
      color: theme.palette.facet.orange,
    },
  },
  serviceValue: {
    color: theme.palette.facet.accentGreen2,
    '& i': {
      color: theme.palette.facet.accentGreen2,
    },
  },
  [theme.breakpoints.down('sm')]: {
    clientCounts: {
      flexDirection: 'row',
      width: '100%',
    },
  },
});


const isProspect = household => household.attr('statusName') === PROSPECT || household.attr('status') === PROSPECT;

const isActive = household => household.attr('statusName') === ACTIVE || household.attr('status') === ACTIVE;

const isActiveOrProspect = household => isActive(household) || isProspect(household);

const isEscalated = household => household.attr('escalated') && isActiveOrProspect(household);

const isInService = household => household.attr('clientStage') === SERVICE && isActive(household);

const MyClients = ({ classes, householdSummaryColl }) => {
  const summaries = householdSummaryColl?.resources().list() || [];

  const total = summaries.filter(isActiveOrProspect).length;
  const onboarding = summaries.filter(isProspect).length;
  const planning = summaries.filter(isActive).length;
  const escalated = summaries.filter(isEscalated).length;
  const service = summaries.filter(isInService).length;

  return (
    <div className={classes.root}>
      <Typography className={classes.title} variant="h1" color="primary">Clients</Typography>
      <div className={classes.clientCounts}>
        <div className={classes.clientCount}>
          <Link to={clientsUri}>
            <div className={classNames(classes.icon, classes.iconTotal)}>
              <img src={totalIcon} alt="Total clients" />
            </div>
            <div className={classNames(classes.countValue, classes.totalValue)}>
              <div>{total}</div>
              <FontAwesome icon="caret-right" test="total" />
            </div>
          </Link>
          <div className={classes.countLabel}>Total Clients</div>
        </div>
        <div className={classes.clientCount}>
          <Link to={`${clientsUri}?clientStage=${ONBOARDING}&status=${PROSPECT}`}>
            {' '}
            <div className={classNames(classes.icon, classes.iconOnboarding)}>
              <img src={onboardingIcon} alt="Onboarding clients" />
            </div>
            <div className={classNames(classes.countValue, classes.onboardingValue)}>
              <div className={classNames(classes.countValue, classes.onboardingValue)}>{onboarding}</div>
              <FontAwesome icon="caret-right" test="onboarding" />
            </div>
          </Link>
          <div className={classes.countLabel}>Onboarding</div>
        </div>
        <div className={classes.clientCount}>
          <Link to={`${clientsUri}?clientStage=${PLANNING}&status=${ACTIVE}`}>
            {' '}
            <div className={classNames(classes.icon, classes.iconPlanning)}>
              <img src={planningIcon} alt="Planning clients" />
            </div>
            <div className={classNames(classes.countValue, classes.planningValue)}>
              <div className={classNames(classes.countValue, classes.planningValue)}>{planning}</div>
              <FontAwesome icon="caret-right" test="planning" />
            </div>
          </Link>
          <div className={classes.countLabel}>Planning</div>
        </div>
        <div className={classes.clientCount}>
          <Link to={`${clientsUri}?clientStage=${SERVICE}&status=${ACTIVE}`}>
            {' '}
            <div className={classNames(classes.icon, classes.iconService)}>
              <img src={serviceIcon} alt="In service clients" />
            </div>
            <div className={classNames(classes.countValue, classes.serviceValue)}>
              <div className={classNames(classes.countValue, classes.serviceValue)}>{service}</div>
              <FontAwesome icon="caret-right" test="service" />
            </div>
          </Link>
          <div className={classes.countLabel}>Service</div>
        </div>
        <div className={classes.clientCount}>
          <Link to={`${clientsUri}?escalated=true`}>
            <div className={classNames(classes.icon, classes.iconEscalated)}>
              <img src={escalatedIcon} alt="Escalated clients" />
            </div>
            <div className={classNames(classes.countValue, classes.escalatedValue)}>
              <div className={classNames(classes.countValue, classes.escalatedValue)}> {escalated}</div>
              <FontAwesome icon="caret-right" test="escalated" />
            </div>
          </Link>
          <div className={classes.countLabel}>Escalated</div>
        </div>
      </div>
    </div>
  );
};

MyClients.propTypes = {
  classes: PropTypes.object.isRequired,
  householdSummaryColl: PropTypes.object.isRequired,
};

export default withStyles(styles)(MyClients);
